import { useEffect, useState } from 'react';
import { Plus, Pencil, Trash2, Merge } from 'lucide-react';
import { apiGet, apiPost, apiPatch, apiDelete } from '../lib/api';
import { useToast } from '../contexts/ToastContext';

interface TagWithCount {
  id: string;
  name: string;
  color: string;
  parent_tag_id: string | null;
  usage_count: number;
}

export function TagsPage() {
  const toast = useToast();
  const [tags, setTags] = useState<TagWithCount[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editingTag, setEditingTag] = useState<TagWithCount | null>(null);
  const [showMerge, setShowMerge] = useState(false);

  async function load() {
    try {
      setTags(await apiGet<TagWithCount[]>('/tags'));
    } catch (err) {
      console.error('Failed to load tags:', err);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function handleCreate(name: string, color: string) {
    try {
      await apiPost('/tags', { name, color });
      setShowCreate(false);
      toast.success('Tag created');
      load();
    } catch (err) {
      console.error('Failed to create tag:', err);
      toast.error('Failed to create tag');
    }
  }

  async function handleUpdate(id: string, name: string, color: string) {
    try {
      await apiPatch(`/tags/${id}`, { name, color });
      setEditingTag(null);
      toast.success('Tag updated');
      load();
    } catch (err) {
      console.error('Failed to update tag:', err);
      toast.error('Failed to update tag');
    }
  }

  async function handleDelete(id: string) {
    if (!window.confirm('Delete this tag? It will be removed from all tasks and notes.')) return;
    try {
      await apiDelete(`/tags/${id}`);
      toast.success('Tag deleted');
      load();
    } catch (err) {
      console.error('Failed to delete tag:', err);
      toast.error('Failed to delete tag');
    }
  }

  async function handleMerge(sourceId: string, targetId: string) {
    try {
      await apiPost('/tags/merge', { source_id: sourceId, target_id: targetId });
      setShowMerge(false);
      toast.success('Tags merged');
      load();
    } catch (err) {
      console.error('Failed to merge tags:', err);
      toast.error('Failed to merge tags');
    }
  }

  if (loading) {
    return (
      <div className="h-full overflow-auto p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-accent border-t-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto p-6">
    <div className="max-w-3xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Tags</h1>
        <div className="flex gap-2">
          {tags.length >= 2 && (
            <button onClick={() => setShowMerge(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-border text-text-secondary text-sm hover:bg-bg-hover transition-colors">
              <Merge size={14} /> Merge
            </button>
          )}
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-accent text-white text-sm font-medium hover:bg-accent-hover transition-colors">
            <Plus size={16} /> New Tag
          </button>
        </div>
      </div>

      {tags.length === 0 ? (
        <div className="text-center py-16 text-text-muted">
          <p className="text-lg mb-2">No tags yet</p>
          <p className="text-sm">Tags help you organize and cross-reference content.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {tags.map(tag => (
            <div key={tag.id} className="flex items-center justify-between px-4 py-3 bg-bg-card border border-border rounded-lg group">
              <div className="flex items-center gap-3">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: tag.color }} />
                <div>
                  <span className="text-sm font-medium text-text-primary">{tag.name}</span>
                  <span className="text-xs text-text-muted ml-2">{tag.usage_count} use{tag.usage_count !== 1 ? 's' : ''}</span>
                </div>
              </div>
              <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => setEditingTag(tag)} className="p-1 text-text-muted hover:text-text-primary"><Pencil size={14} /></button>
                <button onClick={() => handleDelete(tag.id)} className="p-1 text-text-muted hover:text-danger"><Trash2 size={14} /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showCreate || editingTag) && (
        <TagModal
          tag={editingTag}
          onSave={(name, color) => editingTag ? handleUpdate(editingTag.id, name, color) : handleCreate(name, color)}
          onClose={() => { setShowCreate(false); setEditingTag(null); }}
        />
      )}

      {showMerge && (
        <MergeModal tags={tags} onMerge={handleMerge} onClose={() => setShowMerge(false)} />
      )}
    </div>
    </div>
  );
}

const PRESET_COLORS = ['#6366f1', '#ec4899', '#f59e0b', '#22c55e', '#06b6d4', '#8b5cf6', '#ef4444', '#64748b'];

function TagModal({ tag, onSave, onClose }: {
  tag: TagWithCount | null;
  onSave: (name: string, color: string) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(tag?.name ?? '');
  const [color, setColor] = useState(tag?.color ?? '#6366f1');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-4">{tag ? 'Edit Tag' : 'New Tag'}</h2>
        <div className="space-y-4">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Name *</label>
            <input type="text" value={name} onChange={e => setName(e.target.value)} autoFocus placeholder="e.g., infrastructure"
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent text-sm" />
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Color</label>
            <div className="flex gap-2 flex-wrap">
              {PRESET_COLORS.map(c => (
                <button key={c} onClick={() => setColor(c)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${color === c ? 'border-white scale-110' : 'border-transparent'}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">Cancel</button>
          <button onClick={() => name.trim() && onSave(name.trim(), color)} disabled={!name.trim()}
            className="px-4 py-2 text-sm bg-accent text-white rounded-md hover:bg-accent-hover disabled:opacity-50">
            {tag ? 'Save' : 'Create'}
          </button>
        </div>
      </div>
    </div>
  );
}

function MergeModal({ tags, onMerge, onClose }: {
  tags: TagWithCount[];
  onMerge: (sourceId: string, targetId: string) => void;
  onClose: () => void;
}) {
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState('');

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60" onClick={onClose}>
      <div className="bg-bg-surface border border-border rounded-xl p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <h2 className="text-lg font-semibold text-text-primary mb-1">Merge Tags</h2>
        <p className="text-sm text-text-muted mb-4">All content from the source tag will be moved to the target tag. The source tag will be deleted.</p>
        <div className="space-y-3">
          <div>
            <label className="block text-sm text-text-secondary mb-1">Source (will be deleted)</label>
            <select value={sourceId} onChange={e => setSourceId(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary text-sm">
              <option value="">Select tag...</option>
              {tags.filter(t => t.id !== targetId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm text-text-secondary mb-1">Target (will keep)</label>
            <select value={targetId} onChange={e => setTargetId(e.target.value)}
              className="w-full px-3 py-2 bg-bg-input border border-border rounded-md text-text-primary text-sm">
              <option value="">Select tag...</option>
              {tags.filter(t => t.id !== sourceId).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="px-4 py-2 text-sm text-text-secondary hover:text-text-primary">Cancel</button>
          <button onClick={() => sourceId && targetId && onMerge(sourceId, targetId)} disabled={!sourceId || !targetId}
            className="px-4 py-2 text-sm bg-danger text-white rounded-md hover:bg-danger/80 disabled:opacity-50">Merge</button>
        </div>
      </div>
    </div>
  );
}
